import fileService from '../services/fileService';

/**
 * Utility functions to help convert and manage file URLs
 */

/**
 * Convert a Firebase Storage URL to a friendly URL
 * @param {string} firebaseUrl - The Firebase Storage URL
 * @returns {string} - The friendly URL
 */
export const convertToFriendlyUrl = (firebaseUrl) => {
  if (!firebaseUrl) return '';
  
  console.log('Processing URL:', firebaseUrl);
  
  // For now, just return the original Firebase Storage URL as is
  // This will open the Firebase Storage public URL directly
  console.log('Returning original Firebase Storage URL:', firebaseUrl);
  return firebaseUrl;
};

/**
 * The public PDF address of an article. Every article PDF is shown on our own domain as
 * /journal/<friendlyUrl>.pdf (PDFs in Firebase Storage are served there by
 * netlify/edge-functions/article-pdf.js). Running locally there is no edge function, so the
 * Firebase link is used instead.
 * @param {object} article - The article object with fileLink, pdfLink, or nested file data
 * @returns {string} - The best URL to use
 */
export const getBestFileUrl = (article) => {
  if (!article) return '';
  const link = article.fileLink || article.pdfLink || '';
  const friendly = String(article.friendlyUrl || '').replace(/\.pdf$/i, '');
  const isFirebase = link.includes('firebasestorage.googleapis.com');

  if (friendly && (!isFirebase || process.env.NODE_ENV === 'production')) {
    return `/journal/${friendly}.pdf`;
  }
  return link;
};

/**
 * Generate a friendly URL based on article information
 * @param {object} article - The article object
 * @param {string} type - The journal type
 * @returns {string} - The friendly URL
 */
export const generateFriendlyUrl = (article, type = '') => {
  if (!article) return '';
  
  // If we have a title, use it to generate a filename
  if (article.title) {
    const fileName = article.title
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .substring(0, 50);
    
    // Determine folder based on type
    let folder = 'documents';
    if (type === 'editorialNote') {
      folder = 'editorials';
    } else if (type === 'originalArticles' || type === 'reviewArticles') {
      folder = 'journals';
    } else if (type === 'philosophicalInsights') {
      folder = 'philosophy';
    } else if (type === 'seminarReport') {
      folder = 'seminars';
    }
    
    return fileService.getPdfUrl(fileName, folder);
  }
  
  // Fallback to existing URLs
  return getBestFileUrl(article);
};

/**
 * Check if a URL is a Firebase Storage URL
 * @param {string} url - The URL to check
 * @returns {boolean} - Whether it's a Firebase Storage URL
 */
export const isFirebaseStorageUrl = (url) => {
  return url && (url.includes('firebasestorage.googleapis.com') || url.includes('firebasestorage.app/o/'));
};

/**
 * Check if a URL is a friendly URL
 * @param {string} url - The URL to check
 * @returns {boolean} - Whether it's a friendly URL
 */
export const isFriendlyUrl = (url) => {
  return url && url.includes('/docs/');
};

/**
 * Extract file information from a URL
 * @param {string} url - The URL to parse
 * @returns {object} - Object with fileName and folder
 */
export const extractFileInfo = (url) => {
  if (!url) return { fileName: '', folder: '' };
  
  if (isFriendlyUrl(url)) {
    const path = url.split('/docs/')[1];
    if (!path) return { fileName: '', folder: '' };
    
    const parts = path.split('/');
    const fileName = parts.pop();
    const folder = parts.join('/');
    
    return { fileName, folder };
  }
  
  if (isFirebaseStorageUrl(url)) {
    return fileService.parseFirebaseUrl(url);
  }
  
  return { fileName: '', folder: '' };
};
